<template>
  <ai-loyalty-banner
    :title="title"
    md-logo="2"
    offset-md-logo="2"
    md-content="7">
    <template #mainContent>
      <ai-typo
        class="AiHotelLoyaltyBanner-introLabel"
        as="p"
        variant="paragraph-04-regular">
        {{ $t('hotel.loyalty.intro') }}
      </ai-typo>
      <div v-if="notInLoyaltyProgram">
        <ul>
          <li class="AiHotelLoyaltyBanner-item">
            <ai-icon :size="17" name="close" />
            <ai-typo as="p" variant="paragraph-04-regular">
              {{ $t('hotel.loyalty.notInProgram.noPointEarned') }}
            </ai-typo>
          </li>

          <li class="AiHotelLoyaltyBanner-item">
            <ai-icon :size="17" name="close" />
            <ai-typo as="p" variant="paragraph-04-regular">
              {{ $t('hotel.loyalty.notInProgram.noPointUsage') }}
            </ai-typo>
          </li>

          <li class="AiHotelLoyaltyBanner-item">
            <ai-icon :size="17" name="close" />
            <ai-typo as="p" variant="paragraph-04-regular">
              {{ $t('hotel.loyalty.notInProgram.noBenefits') }}
            </ai-typo>
          </li>

          <li class="AiHotelLoyaltyBanner-item">
            <ai-icon :size="17" name="close" />
            <ai-typo as="p" variant="paragraph-04-regular">
              {{ $t('hotel.loyalty.notInProgram.noMemberRates') }}
            </ai-typo>
          </li>
        </ul>
      </div>

      <div v-else>
        <ai-typo
          v-if="logged"
          class="AiHotelLoyaltyBanner-description"
          as="p"
          variant="paragraph-04-regular">
          {{
            $t('hotel.loyalty.text.connectText', {
              status: userStore.loyaltyStatus,
            })
          }}
        </ai-typo>

        <ul>
          <!-- Earn allowed -->
          <li class="AiHotelLoyaltyBanner-item">
            <ai-icon :size="18" :name="earnAllowed ? 'check-mark' : 'close'" />

            <ai-typo as="p" variant="paragraph-04-regular">
              {{
                $t(
                  earnAllowed
                    ? 'hotel.loyalty.text.earnings'
                    : 'hotel.loyalty.notInProgram.noPointEarned',
                )
              }}
            </ai-typo>
          </li>

          <!-- Burn allowed -->
          <li class="AiHotelLoyaltyBanner-item">
            <ai-icon :size="18" :name="burnAllowed ? 'check-mark' : 'close'" />

            <ai-typo as="p" variant="paragraph-04-regular">
              {{
                $t(
                  burnAllowed
                    ? 'hotel.loyalty.text.burn'
                    : 'hotel.loyalty.text.notInProgram.noPointUsage',
                )
              }}
            </ai-typo>
          </li>

          <!-- Advantage allowed -->
          <li class="AiHotelLoyaltyBanner-item">
            <ai-icon
              :size="18"
              :name="advantageAllowed ? 'check-mark' : 'close'" />

            <ai-typo as="p" variant="paragraph-04-regular">
              {{
                $t(
                  advantageAllowed
                    ? logged
                      ? 'hotel.loyalty.text.loggedBenefits'
                      : 'hotel.loyalty.text.benefits'
                    : 'hotel.loyalty.text.notInProgram.noBenefits',
                  {
                    status: userStatus,
                  },
                )
              }}
            </ai-typo>
          </li>

          <!-- Member rate -->
          <li class="AiHotelLoyaltyBanner-item">
            <ai-icon
              :size="18"
              :name="memberRateInHotel ? 'check-mark' : 'close'" />

            <ai-typo
              v-if="memberRateInHotel"
              as="p"
              variant="paragraph-04-regular">
              {{ $t('hotel.loyalty.text.advantageMember') }}
            </ai-typo>

            <ai-typo v-else as="p" variant="paragraph-04-regular">
              {{ $t('hotel.loyalty.notInProgram.noMemberRates') }}
            </ai-typo>
          </li>
        </ul>

        <ai-typo
          class="AiHotelLoyaltyBanner-moreLabel"
          as="p"
          variant="paragraph-04-regular">
          {{ $t('hotel.loyalty.label') }}
        </ai-typo>
      </div>
    </template>

    <template #actionContent>
      <div v-if="!logged" class="AiHotelLoyaltyBanner-button">
        <ai-button
          as-link
          :url="signupUrl"
          class="AiHotelLoyaltyBanner-memberButton"
          :label="$t('hotel.loyalty.footer.memberButtonLabel')"
          variant="secondary"
          :aria-label="$t('accessibility.description.becomeMemberButton')"
          @click="$emit('become-member-click')" />

        <div
          class="AiHotelLoyaltyBanner-loginButton"
          :aria-label="$t('accessibility.description.disconnectButton')">
          <ai-typo
            class="AiHotelLoyaltyBanner-loginButtonLabel"
            as="p"
            variant="paragraph-04-regular">
            {{ $t('hotel.loyalty.footer.label') }}
          </ai-typo>

          <ai-button
            as-link
            :url="loginUrl"
            slim
            :label="$t('hotel.loyalty.footer.logButtonLabel')"
            variant="tertiary"
            :aria-label="$t('accessibility.description.loginButton')" />
        </div>
      </div>

      <div v-else class="AiHotelLoyaltyBanner-logout">
        <ai-typo
          as="p"
          variant="paragraph-04-regular"
          class="AiHotelLoyaltyBanner-logoutButton">
          {{ $t('hotel.loyalty.footer.not') }}
        </ai-typo>

        <ai-button
          slim
          :label="$t('hotel.loyalty.footer.logOutButtonLabel')"
          variant="tertiary"
          :aria-label="$t('accessibility.description.disconnectButton')"
          @click="logout" />
      </div>
    </template>
  </ai-loyalty-banner>
</template>

<script setup lang="ts">
import { HotelLoyaltyStatus } from '../../constants';

import type { LoyaltyProgram } from '~~/domains/graphql';
import { useUserStore } from '~~/domains/user';
import { getRedirectUrl } from '~~/domains/ux/helpers/loyaltyBanner';

type Props = {
  loyalty?: LoyaltyProgram | null;
};

const props = defineProps<Props>();

type Emits = {
  (event: 'become-member-click'): void;
};
defineEmits<Emits>();

const config = useRuntimeConfig();
const route = useRoute();
const userStore = useUserStore();
const { t, locale } = useI18n();

const signupUrl = computed(() =>
  getRedirectUrl(
    config.public.accorAuthenticationUrl,
    config.public.baseUrl,
    locale.value,
    {
      redirect: route.fullPath.toString(),
      type: 'signup',
    },
    true,
  ).toString(),
);

const loginUrl = computed(() =>
  getRedirectUrl(
    config.public.accorAuthenticationUrl,
    config.public.baseUrl,
    locale.value,
    {
      redirect: route.fullPath.toString(),
      type: 'login',
    },
  ).toString(),
);

const logout = () => {
  userStore.logout();
};

const logged = computed(() => userStore.isLogged);

const title = computed<string>(() => {
  if (notInLoyaltyProgram.value) {
    return t('hotel.loyalty.title.nonParticipatingResort');
  } else if (logged.value && userStatus.value) {
    return t('hotel.loyalty.title.logged', { loyaltyStatus: userStatus.value });
  }

  return t('hotel.loyalty.title.anonymous');
});

const loyaltyStatus = computed(() => props.loyalty?.status);
const earnAllowed = computed(() => props.loyalty?.earnAllowed);
const burnAllowed = computed(() => props.loyalty?.burnAllowed);
const advantageAllowed = computed(() => props.loyalty?.advantageAllowed);
const memberRateInHotel = computed(() => props.loyalty?.memberRate);
const notInLoyaltyProgram = computed(() =>
  [HotelLoyaltyStatus.Unknown, HotelLoyaltyStatus.NonParticipating].includes(
    loyaltyStatus.value as string,
  ),
);

const userStatus = computed(
  () => userStore.activeLoyaltyCard?.cardProduct?.productTier ?? '',
);
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/mq';
@use '@/assets/styles/utilities/mixins';

.AiIcon--check-mark,
.AiIcon--close {
  margin-right: constants.$margin-00;
}

.AiHotelLoyaltyBanner-item {
  display: flex;
  text-align: center;
  flex-direction: column;
  align-items: center;
  margin-bottom: constants.$inner-02;

  @media (mq.$from-medium) {
    text-align: start;
    flex-direction: row;
  }

  &:last-child {
    margin-bottom: constants.$margin-01;

    @media (mq.$from-medium) {
      margin-bottom: constants.$margin-02;
    }
  }
}

.AiHotelLoyaltyBanner-moreLabel {
  margin-bottom: constants.$margin-04;
  text-align: center;

  @media (mq.$from-medium) {
    text-align: start;
  }
}

.AiHotelLoyaltyBanner-introLabel {
  margin-bottom: constants.$margin-01;
  text-align: center;

  @media (mq.$from-medium) {
    text-align: start;
  }
}

.AiHotelLoyaltyBanner-button {
  display: flex;
  flex-direction: column;
  align-items: center;
  @media (mq.$from-medium) {
    flex-direction: row;
  }

  @media (mq.$medium) {
    align-items: flex-end;
  }
}

.AiHotelLoyaltyBanner-memberButton {
  width: 100%;
  margin-bottom: constants.$margin-02;

  @media (mq.$from-medium) {
    margin-right: constants.$margin-02;
  }

  @media (mq.$from-large) {
    width: auto;
    margin-bottom: 0;
  }
}

.AiHotelLoyaltyBanner-loginButton {
  text-align: center;

  @media (mq.$from-medium) {
    text-align: start;
  }
}

.AiHotelLoyaltyBanner-loginButtonLabel {
  margin-bottom: constants.$inner-02;

  @media (mq.$medium) {
    margin-bottom: 0;
  }
}

.AiHotelLoyaltyBanner-logout {
  display: flex;
  flex-direction: column;
  text-align: center;

  @media (mq.$from-medium) {
    display: block;
    text-align: left;
  }
}

.AiHotelLoyaltyBanner-logoutButton {
  margin-bottom: constants.$margin-01;

  @media (mq.$from-medium) {
    display: block;
    margin-bottom: constants.$margin-00;
  }
}
</style>
